/**
 * O desenho vetorial de uma planta — tipos, cores e a renderização das formas.
 *
 * Mora fora do editor porque TRÊS lugares desenham as mesmas formas: o
 * editor, a planta em modo leitura e o relatório em PDF. Uma cópia por lugar
 * divergiria no dia em que alguém ajustasse a espessura de uma parede, e o
 * sintoma seria a planta impressa não bater com a da tela — num documento de
 * conformidade, isso é pior que feio.
 *
 * COORDENADAS. As formas vivem no espaço do documento (`largura` × `altura`),
 * que vira o `viewBox` do SVG. Os marcadores dos ativos continuam em fração
 * 0..1 do quadro inteiro, como sempre foram: são dois sistemas separados de
 * propósito, pra que trocar o desenho por um maior não mova marcador nenhum.
 */

export type Comodo = {
  id: string; tipo: "comodo";
  x: number; y: number; w: number; h: number;
  rotulo: string; cor: CorDeComodo;
};
export type Parede = {
  id: string; tipo: "parede";
  x1: number; y1: number; x2: number; y2: number;
};
export type Porta = {
  id: string; tipo: "porta";
  x: number; y: number; largura: number; angulo: number;
};
export type Rotulo = {
  id: string; tipo: "rotulo";
  x: number; y: number; texto: string;
};

export type Forma = Comodo | Parede | Porta | Rotulo;

export interface Desenho {
  versao: 1;
  largura: number;
  altura: number;
  /** Tema do papel. Ausente = claro, que é o que os desenhos da v41 têm. */
  tema?: TemaDaPlanta;
  formas: Forma[];
}

/**
 * Paleta fechada, e fechada de propósito.
 *
 * Cor livre deixaria alguém pintar um cômodo com a cor de um ESTADO de ativo
 * — crítico, atenção, sem comunicação. Aí o desenho passa a afirmar algo
 * sobre a operação, que é justamente o que os marcadores dizem, e os dois
 * podem se contradizer na mesma imagem.
 */
export const ROTULO_DA_COR = {
  neutro:    "Neutro",
  frio:      "Refrigerado",
  seco:      "Seco",
  expedicao: "Expedição",
  restrito:  "Restrito",
} as const;

export type CorDeComodo = keyof typeof ROTULO_DA_COR;

/**
 * Os temas do PAPEL — claro, preto e navy.
 *
 * Isto é do documento, não do app: uma planta pendurada numa TV no salão
 * quer fundo escuro, e a mesma planta impressa quer fundo branco. Guardar a
 * escolha no desenho é o que faz a tela, o painel e o PDF concordarem.
 *
 * NO ESCURO, TODO TRAÇO DE CÔMODO É NEUTRO. No claro cada tipo tem contorno
 * da própria família, mas no escuro um contorno verde ou âmbar ficaria a um
 * passo das cores de ESTADO do ativo — e de três metros, um cômodo com
 * contorno verde lido como "zona ok" é uma afirmação que o desenho não tem
 * direito de fazer. A distinção entre tipos sobrevive no preenchimento.
 *
 * Os valores foram medidos, não escolhidos no olho: traço e texto ficam
 * acima de 12:1 contra o papel escuro, texto sobre cômodo acima de 9,7:1, e
 * os preenchimentos entre 1,1 e 1,5 — fundo tem que ser fundo, porque quem
 * precisa ganhar o olho nesta tela é o marcador do ativo.
 */
export const TEMAS = {
  claro: {
    rotulo: "Claro",
    papel: "#ffffff", tinta: "#334155", traco: "#475569", grade: "#e2e8f0",
    comodos: {
      neutro:    { preenche: "#e2e8f0", traco: "#94a3b8" },
      frio:      { preenche: "#dbeafe", traco: "#93c5fd" },
      seco:      { preenche: "#fef3c7", traco: "#fcd34d" },
      expedicao: { preenche: "#dcfce7", traco: "#86efac" },
      restrito:  { preenche: "#fae8ff", traco: "#e9d5ff" },
    },
  },
  preto: {
    rotulo: "Preto",
    papel: "#0a0a0a", tinta: "#e2e8f0", traco: "#cbd5e1", grade: "#262626",
    comodos: {
      neutro:    { preenche: "#1f2937", traco: "#cbd5e1" },
      frio:      { preenche: "#14304d", traco: "#cbd5e1" },
      seco:      { preenche: "#3b2f14", traco: "#cbd5e1" },
      expedicao: { preenche: "#123526", traco: "#cbd5e1" },
      restrito:  { preenche: "#2d1b3d", traco: "#cbd5e1" },
    },
  },
  navy: {
    rotulo: "Azul-marinho",
    papel: "#0b1a33", tinta: "#e0ecff", traco: "#bfdbfe", grade: "#1c3157",
    comodos: {
      neutro:    { preenche: "#1c2b45", traco: "#bfdbfe" },
      frio:      { preenche: "#123a63", traco: "#bfdbfe" },
      seco:      { preenche: "#3d3116", traco: "#bfdbfe" },
      expedicao: { preenche: "#123b2a", traco: "#bfdbfe" },
      restrito:  { preenche: "#33204a", traco: "#bfdbfe" },
    },
  },
} as const;

export type TemaDaPlanta = keyof typeof TEMAS;

export function tema(nome: TemaDaPlanta | undefined) {
  return TEMAS[nome ?? "claro"] ?? TEMAS.claro;
}

/**
 * O quadro nasce 16:9.
 *
 * Não é estética: a tela de quem desenha é 16:9 e a TV onde a planta vai
 * parar é 16:9. Quadro em outra proporção sobra numa direção e falta na
 * outra — e o que sobra vira barra de rolagem na hora de desenhar, que é
 * como o traço sai torto.
 */
export const LADO_PADRAO = { largura: 1600, altura: 900 };

/** Passo da grade, em unidades do documento. */
export const GRADE = 20;

export function encaixar(v: number, ligada: boolean): number {
  return ligada ? Math.round(v / GRADE) * GRADE : Math.round(v);
}

export function desenhoVazio(): Desenho {
  return { versao: 1, ...LADO_PADRAO, tema: "claro", formas: [] };
}

export function novoId(): string {
  // `crypto.randomUUID` não existe em contexto não seguro (http em rede
  // local), e o editor precisa funcionar lá — é onde ficam os gateways.
  return globalThis.crypto?.randomUUID?.()
    ?? `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** O arco de abertura de uma porta, em `path` SVG. */
export function caminhoDaPorta(p: Porta): string {
  const r = p.largura;
  const rad = (p.angulo * Math.PI) / 180;
  const fx = p.x + r * Math.cos(rad);
  const fy = p.y + r * Math.sin(rad);
  const ax = p.x + r * Math.cos(rad + Math.PI / 2);
  const ay = p.y + r * Math.sin(rad + Math.PI / 2);
  // Folha da porta + arco de varredura: é o símbolo que qualquer pessoa que
  // já viu uma planta reconhece, e ele diz PRA QUE LADO a porta abre — que
  // numa câmara fria é informação de operação, não enfeite.
  return `M ${p.x} ${p.y} L ${fx} ${fy} M ${fx} ${fy} A ${r} ${r} 0 0 1 ${ax} ${ay}`;
}

/**
 * Caixa envolvente de uma forma, em unidades do documento.
 * Usada pra seleção e pra manter tudo dentro do quadro.
 */
export function caixaDaForma(f: Forma): { x: number; y: number; w: number; h: number } {
  switch (f.tipo) {
    case "comodo":
      return { x: f.x, y: f.y, w: f.w, h: f.h };
    case "parede":
      return {
        x: Math.min(f.x1, f.x2), y: Math.min(f.y1, f.y2),
        w: Math.abs(f.x2 - f.x1), h: Math.abs(f.y2 - f.y1),
      };
    case "porta":
      return { x: f.x - f.largura, y: f.y - f.largura, w: f.largura * 2, h: f.largura * 2 };
    case "rotulo":
      return { x: f.x, y: f.y - 14, w: Math.max(40, f.texto.length * 9), h: 20 };
  }
}

export function moverForma(f: Forma, dx: number, dy: number): Forma {
  switch (f.tipo) {
    case "comodo": return { ...f, x: f.x + dx, y: f.y + dy };
    case "parede": return { ...f, x1: f.x1 + dx, y1: f.y1 + dy, x2: f.x2 + dx, y2: f.y2 + dy };
    case "porta":  return { ...f, x: f.x + dx, y: f.y + dy };
    case "rotulo": return { ...f, x: f.x + dx, y: f.y + dy };
  }
}


/**
 * Distância de um ponto a um SEGMENTO (não à reta que o contém).
 *
 * A diferença importa: a reta é infinita, e clicar no prolongamento de uma
 * parede, do outro lado do salão, selecionaria a parede.
 */
export function distanciaAoSegmento(
  px: number, py: number,
  x1: number, y1: number, x2: number, y2: number,
): number {
  const dx = x2 - x1, dy = y2 - y1;
  const comprimento2 = dx * dx + dy * dy;
  // Parede de comprimento zero não existe, mas um clique sobre ela não pode
  // dividir por zero.
  if (comprimento2 === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / comprimento2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/**
 * O ponto acertou a forma?
 *
 * POR QUE NÃO É SÓ A CAIXA ENVOLVENTE, que era como estava: uma parede
 * horizontal tem caixa de ALTURA ZERO, e uma vertical, de largura zero. O
 * teste de caixa exigia acertar a coordenada exata — na prática, parede
 * desenhada não podia mais ser selecionada. Com o encaixe na grade ligado,
 * que é o padrão, toda parede sai perfeitamente reta e cai nesse caso.
 *
 * `tolerancia` vem em unidades do DOCUMENTO e é calculada pelo editor a
 * partir da escala na tela, pra que o alvo tenha sempre o mesmo tamanho em
 * pixels — um documento grande desenhado pequeno precisa de tolerância
 * maior, não menor.
 */
export function atingiu(
  f: Forma, px: number, py: number, tolerancia: number,
): boolean {
  switch (f.tipo) {
    case "parede":
      return distanciaAoSegmento(px, py, f.x1, f.y1, f.x2, f.y2) <= tolerancia;
    case "porta":
      // O alvo é a folha e o arco, que cabem num disco de raio `largura`.
      return Math.hypot(px - f.x, py - f.y) <= f.largura + tolerancia;
    default: {
      // Cômodo e rótulo são retângulos — mas um cômodo pode ser desenhado
      // fino, então a caixa também é inflada.
      const c = caixaDaForma(f);
      return px >= c.x - tolerancia && px <= c.x + c.w + tolerancia
          && py >= c.y - tolerancia && py <= c.y + c.h + tolerancia;
    }
  }
}


/**
 * As alças de redimensionamento de uma forma.
 *
 * Sem elas, mudar o tamanho de um cômodo exigia apagar e desenhar de novo —
 * e quem já nomeou o cômodo e escolheu a cor perde as duas coisas. Na
 * prática o desenho para de ser ajustado, que é o começo da planta que
 * envelhece mentindo.
 */
export const TAMANHO_MINIMO = 20;

export interface Alca { id: string; x: number; y: number }

export function alcasDaForma(f: Forma): Alca[] {
  switch (f.tipo) {
    case "comodo":
      return [
        { id: "no", x: f.x,         y: f.y },
        { id: "ne", x: f.x + f.w,   y: f.y },
        { id: "so", x: f.x,         y: f.y + f.h },
        { id: "se", x: f.x + f.w,   y: f.y + f.h },
      ];
    case "parede":
      return [
        { id: "a", x: f.x1, y: f.y1 },
        { id: "b", x: f.x2, y: f.y2 },
      ];
    case "porta":
      // Uma alça na ponta da folha: arrastar muda o tamanho E o lado de
      // abertura, que é como se pensa numa porta — não em "raio" e "ângulo".
      return [{
        id: "abertura",
        x: f.x + f.largura * Math.cos((f.angulo * Math.PI) / 180),
        y: f.y + f.largura * Math.sin((f.angulo * Math.PI) / 180),
      }];
    case "rotulo":
      // Texto não redimensiona: o tamanho da fonte é do desenho, não da
      // frase, senão cada rótulo vira uma decisão.
      return [];
  }
}

/**
 * A forma com a alça `alca` levada até (px, py).
 *
 * O CÔMODO NUNCA INVERTE. Arrastar a alça superior-esquerda para além da
 * inferior-direita daria largura negativa, que o banco recusa (CHECK de
 * 0..1 depois da conversão) e que na tela vira retângulo invisível. Aqui o
 * mínimo segura, e o lado oposto fica onde está.
 */
export function redimensionar(
  f: Forma, alca: string, px: number, py: number, grade: boolean,
): Forma {
  const x = encaixar(px, grade);
  const y = encaixar(py, grade);

  switch (f.tipo) {
    case "comodo": {
      const dir = f.x + f.w;
      const base = f.y + f.h;
      const oeste = alca === "no" || alca === "so";
      const norte = alca === "no" || alca === "ne";

      const novoX = oeste ? Math.min(x, dir - TAMANHO_MINIMO) : f.x;
      const novoY = norte ? Math.min(y, base - TAMANHO_MINIMO) : f.y;
      const novaL = oeste ? dir - novoX : Math.max(TAMANHO_MINIMO, x - f.x);
      const novaA = norte ? base - novoY : Math.max(TAMANHO_MINIMO, y - f.y);

      return { ...f, x: novoX, y: novoY, w: novaL, h: novaA };
    }
    case "parede":
      return alca === "a" ? { ...f, x1: x, y1: y } : { ...f, x2: x, y2: y };
    case "porta": {
      const dx = px - f.x;
      const dy = py - f.y;
      const largura = Math.max(TAMANHO_MINIMO, Math.round(Math.hypot(dx, dy)));
      // Ângulo em passos de 15°, que é o mesmo passo do controle deslizante
      // — sem isso a porta fica num ângulo quebrado que ninguém escolheu.
      const graus = Math.round((Math.atan2(dy, dx) * 180) / Math.PI / 15) * 15;
      return { ...f, largura, angulo: graus };
    }
    case "rotulo":
      return f;
  }
}
